<template>
  <div>
    <Navbar />

    <!-- Contact content -->
    <section
      id="contact"
      class="relative py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900"
    >
      <div class="container relative mt-10">
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div class="space-y-6 lg:col-span-5">
            <div
              class="p-6 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary shrink-0"
                >
                  <i class="text-2xl mdi mdi-headset"></i>
                </div>
                <div class="space-y-1">
                  <h4
                    class="text-lg font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("contact_support_title") }}
                  </h4>
                  <p class="text-sm text-slate-600 dark:text-slate-400">
                    {{ t("contact_support_desc") }}
                  </p>
                </div>
              </div>

              <div class="pt-4 space-y-2 text-sm">
                <div
                  class="flex items-center gap-2 text-slate-700 dark:text-slate-200"
                >
                  <i
                    class="mdi mdi-phone-outline text-secondary text-[18px]"
                  ></i>
                  <span dir="ltr">+966 509212155</span>
                </div>
                <div
                  class="flex items-center gap-2 text-slate-700 dark:text-slate-200"
                >
                  <i
                    class="mdi mdi-email-outline text-secondary text-[18px]"
                  ></i>
                  <span>hr@mharat.com.sa</span>
                </div>
              </div>
            </div>

            <!-- Card: Office -->
            <div
              class="p-6 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary shrink-0"
                >
                  <i class="text-2xl mdi mdi-map-marker-outline"></i>
                </div>
                <div class="space-y-1">
                  <h4
                    class="text-lg font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("contact_office_title") }}
                  </h4>
                  <p class="text-sm text-slate-600 dark:text-slate-400">
                    {{ t("contact_office_desc") }}
                  </p>
                </div>
              </div>

              <div
                class="pt-4 text-sm leading-relaxed text-slate-700 dark:text-slate-200"
              >
                {{ t("contact_office_address") }}
              </div>

              <!-- Map placeholder -->
              <div
                class="mt-4 overflow-hidden border rounded-2xl border-slate-200 dark:border-slate-700"
              >
                <div
                  class="flex items-center justify-center h-48 bg-slate-100 dark:bg-slate-800 text-slate-500"
                >
                  <i class="text-3xl mdi mdi-google-maps me-2"></i>
                  {{ t("contact_map_placeholder") }}
                </div>
              </div>
            </div>

            <!-- Social -->
            <div
              class="p-6 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
            >
              <h4
                class="mb-3 text-base font-semibold text-slate-900 dark:text-white"
              >
                {{ t("contact_social_title") }}
              </h4>
              <div class="flex flex-wrap gap-2">
                <a
                  href="#"
                  class="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold transition border rounded-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-secondary/20 hover:text-primary"
                >
                  <i class="mdi mdi-twitter text-[16px]"></i> X / Twitter
                </a>
                <a
                  href="#"
                  class="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold transition border rounded-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-secondary/20 hover:text-primary"
                >
                  <i class="mdi mdi-linkedin text-[16px]"></i> LinkedIn
                </a>
                <a
                  href="#"
                  class="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold transition border rounded-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-secondary/20 hover:text-primary"
                >
                  <i class="mdi mdi-instagram text-[16px]"></i> Instagram
                </a>
              </div>
            </div>
          </div>

          <!-- Right: Contact Form -->
          <div class="lg:col-span-7">
            <div
              class="relative p-6 md:p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-[28px] shadow-sm"
            >
              <!-- faint identity shape -->
              <div
                class="pointer-events-none absolute -top-8 -end-8 w-40 h-40 rounded-[60px] bg-secondary/20 blur-2xl hidden sm:block"
              ></div>

              <h3
                class="text-xl font-bold md:text-2xl text-slate-900 dark:text-white"
              >
                {{ t("contact_form_title") }}
              </h3>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {{ t("contact_form_desc") }}
              </p>

              <form
                class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2"
                @submit.prevent="submit"
              >
                <!-- Name -->
                <div class="md:col-span-1">
                  <label
                    class="text-xs font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {{ t("contact_company_name") }}
                  </label>
                  <input
                    v-model="form.company_name"
                    type="text"
                    class="w-full px-4 mt-1 text-sm border h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-secondary/60 focus:outline-none text-slate-800 dark:text-white"
                    :placeholder="t('contact_company_name_ph')"
                  />
                  <p
                    v-if="errors.company_name"
                    class="mt-1 text-xs text-rose-600"
                  >
                    {{ errors.company_name[0] }}
                  </p>
                </div>

                <!-- Email -->
                <div class="md:col-span-1">
                  <label
                    class="text-xs font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {{ t("contact_email") }}
                  </label>
                  <input
                    v-model="form.email"
                    type="email"
                    class="w-full px-4 mt-1 text-sm border h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-secondary/60 focus:outline-none text-slate-800 dark:text-white"
                    :placeholder="t('contact_email_ph')"
                  />
                </div>

                <!-- Phone -->
                <div class="md:col-span-1">
                  <label
                    class="text-xs font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {{ t("contact_phone") }}
                  </label>
                  <input
                    v-model="form.phone"
                    type="text"
                    class="w-full px-4 mt-1 text-sm border h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-secondary/60 focus:outline-none text-slate-800 dark:text-white"
                    :placeholder="t('contact_phone_ph')"
                  />
                </div>

                <!-- Subject -->
                <div class="md:col-span-1">
                  <label
                    class="text-xs font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {{ t("contact_subject") }}
                  </label>
                  <input
                    v-model="form.subject"
                    type="text"
                    class="w-full px-4 mt-1 text-sm border h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-secondary/60 focus:outline-none text-slate-800 dark:text-white"
                    :placeholder="t('contact_subject_ph')"
                  />
                  <p v-if="errors.subject" class="mt-1 text-xs text-rose-600">
                    {{ errors.subject[0] }}
                  </p>
                </div>

                <!-- Message -->
                <div class="md:col-span-2">
                  <label
                    class="text-xs font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {{ t("contact_message") }}
                  </label>
                  <textarea
                    v-model="form.message"
                    rows="6"
                    class="w-full px-4 py-3 mt-1 text-sm border resize-none rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-secondary/60 focus:outline-none text-slate-800 dark:text-white"
                    :placeholder="t('contact_message_ph')"
                  ></textarea>
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-3 pt-2 md:col-span-2">
                  <button
                    type="submit"
                    :disabled="isSubmitting"
                    class="inline-flex items-center justify-center w-full font-semibold tracking-wide text-white transition rounded-md shadow-md md:w-auto h-11 px-7 bg-primary hover:bg-primary-light disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i class="mdi mdi-send-outline text-[18px] me-2"></i>
                    {{ isSubmitting ? t("loading") : t("contact_send") }}
                  </button>

                  <p
                    v-if="sent"
                    class="text-sm text-emerald-600 dark:text-emerald-400"
                  >
                    {{ t("contact_sent") }}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import { messages } from "@/i18n";
import Navbar from "@/components/Navbar.vue";
import Footer from "@/components/Footer.vue";
import api from "@/services/api";
import Toast from "@/components/Toast.vue";

export default {
  components: { Navbar, Footer, Toast },

  data() {
    return {
      messages,
      lang: (
        document.documentElement.lang ||
        localStorage.getItem("app_lang") ||
        "ar"
      )
        .toLowerCase()
        .startsWith("en")
        ? "en"
        : "ar",

      _langObserver: null,

      form: {
        company_name: "",
        subject: "",
        email: "",
        phone: "",
        message: "",
      },

      sent: false,
      isSubmitting: false,
      errors: {},

      toast: {
        show: false,
        type: "success",
        message: "",
      },
    };
  },

  mounted() {
    // sync lang with <html lang="">
    this._langObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "lang") {
          const htmlLang = document.documentElement.lang || "ar";
          this.lang = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
        }
      }
    });
    this._langObserver.observe(document.documentElement, { attributes: true });
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },

    async submit() {
      if (this.isSubmitting) return;

      this.isSubmitting = true;
      this.sent = false;
      this.errors = {};

      try {
        const payload = {
          company_name: this.form.company_name,
          subject: this.form.subject,
          email: this.form.email || null,
          phone: this.form.phone || null,
          message: this.form.message,
        };

        await api.post("/contact-messages", payload);

        // ✅ success toast
        this.toast = {
          show: true,
          type: "success",
          message: this.t("contact_sent") || "تم إرسال الرسالة بنجاح ✅",
        };

        // reset after success
        this.form = {
          company_name: "",
          subject: "",
          email: "",
          phone: "",
          message: "",
        };
      } catch (e) {
        if (e?.response?.status === 422) {
          this.errors = e.response.data.errors || {};

          // optional validation toast
          this.toast = {
            show: true,
            type: "error",
            message:
              this.t("contact_error_validation") || "تحقق من الحقول المطلوبة",
          };
        } else {
          console.error(e);

          this.toast = {
            show: true,
            type: "error",
            message:
              this.t("contact_error_generic") || "حدث خطأ، حاول مرة أخرى",
          };
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
